import React, { useEffect, useMemo, useState } from 'react';

const TOOL_LOGO_PLACEHOLDER = '/images/tool-placeholder.svg';

const normalizeDomain = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  try {
    const candidate = raw.includes('://') ? raw : `https://${raw}`;
    return new URL(candidate).hostname.replace(/^www\./i, '');
  } catch {
    return raw
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split('/')[0]
      .trim();
  }
};

const buildLogoDevUrl = (domain) => {
  const token = (process.env.REACT_APP_LOGO_DEV_TOKEN || '').trim();
  const safeDomain = encodeURIComponent(domain);
  return token
    ? `https://img.logo.dev/${safeDomain}?token=${encodeURIComponent(token)}`
    : `https://img.logo.dev/${safeDomain}`;
};

const buildGoogleFaviconUrl = (domain) =>
  `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;

const ToolLogo = ({ domain, alt, className = '' }) => {
  const normalizedDomain = useMemo(() => normalizeDomain(domain), [domain]);
  const [currentSrc, setCurrentSrc] = useState(
    normalizedDomain ? buildLogoDevUrl(normalizedDomain) : TOOL_LOGO_PLACEHOLDER
  );
  const [useGoogleFallback, setUseGoogleFallback] = useState(false);

  useEffect(() => {
    if (!normalizedDomain) {
      setCurrentSrc(TOOL_LOGO_PLACEHOLDER);
      setUseGoogleFallback(false);
      return;
    }
    setCurrentSrc(buildLogoDevUrl(normalizedDomain));
    setUseGoogleFallback(false);
  }, [normalizedDomain]);

  const handleError = () => {
    if (!normalizedDomain) {
      setCurrentSrc(TOOL_LOGO_PLACEHOLDER);
      return;
    }

    if (!useGoogleFallback) {
      setCurrentSrc(buildGoogleFaviconUrl(normalizedDomain));
      setUseGoogleFallback(true);
      return;
    }

    setCurrentSrc(TOOL_LOGO_PLACEHOLDER);
  };

  return (
    <img
      src={currentSrc}
      alt={alt || normalizedDomain || 'Tool logo'}
      onError={handleError}
      loading="lazy"
      className={`w-12 h-12 rounded-xl object-contain bg-white p-1 border border-slate-200 ${className}`.trim()}
    />
  );
};

export default ToolLogo;
